import React, { useState } from "react";
import {
  testTranscribeAndAnalyze,
  downloadTranscription,
  downloadAnalysis,
} from "../../api";

const TestTranscribePage = () => {
  const [apiKey, setApiKey] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
  };

  const sanitizeFilename = (v) => {
    if (!v || typeof v !== "string") return "";
    const clean = v.split("?")[0].split("#")[0];
    const parts = clean.split("/").filter(Boolean);
    return parts.pop() || "";
  };

  const parseDispositionFilename = (header) => {
    if (!header) return "";
    try {
      const mStar = header.match(/filename\*=UTF-8''([^;\n]+)/i);
      if (mStar && mStar[1]) return decodeURIComponent(mStar[1]);
      const m = header.match(/filename="?([^";]+)"?/i);
      if (m && m[1]) return m[1];
    } catch {}
    return "";
  };

  const handleDownload = async (url, filename) => {
    if (!url) return;
    const href = toAbsoluteApiUrl(url);
    try {
      const resp = await fetch(href, {
        method: "GET",
        headers: apiKey ? { "X-API-Key": apiKey } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const cd = resp.headers.get("content-disposition");
      const name = filename || parseDispositionFilename(cd) || extractFilenameFromUrl(href) || "download.txt";
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    } catch (e) {
      // Фолбэк: открываем в новой вкладке (если доступ без заголовка)
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  const toAbsoluteApiUrl = (s) => {
    if (!s || typeof s !== "string") return s;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    if (s.startsWith("/")) {
      try {
        const origin = new URL(downloadAnalysis("x")).origin;
        return origin + s;
      } catch {
        return s;
      }
    }
    return s;
  };

  const extractFilenameFromUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    try {
      const u = new URL(url, window.location.origin);
      const parts = (u.pathname || "").split("/").filter(Boolean);
      if (parts.length === 0) return "";
      // Если последний сегмент 'download', берем предпоследний как имя файла
      const last = parts[parts.length - 1];
      const filename = last.toLowerCase() === "download" && parts.length >= 2
        ? parts[parts.length - 2]
        : last;
      return decodeURIComponent(filename || "");
    } catch {
      // Ручной разбор без URL()
      const clean = url.split("?")[0].split("#")[0];
      const parts = clean.split("/").filter(Boolean);
      if (!parts.length) return "";
      const last = parts[parts.length - 1];
      const filename = last.toLowerCase() === "download" && parts.length >= 2
        ? parts[parts.length - 2]
        : last;
      try { return decodeURIComponent(filename); } catch { return filename; }
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!apiKey) {
      setError("Укажите API ключ");
      return;
    }
    if (!file) {
      setError("Выберите MP3 файл");
      return;
    }

    setLoading(true);
    try {
      const data = await testTranscribeAndAnalyze(apiKey, file);

      // Бэкенд может вернуть полезные поля как на верхнем уровне, так и внутри data
      const payload = (data && typeof data === "object" && data.data) ? data.data : data;

      // Доп. поддержка ответа вида { download_urls: { transcription: url, analysis: url } }
      const downloadUrls = payload.download_urls || payload.urls || null;
      // Поддержка вложенных блоков { transcription: { filename, download_url }, analysis: { filename, download_url } }
      const tBlock = payload.transcription || payload.transcript || null;
      const aBlock = payload.analysis || payload.analyse || payload.analytics || null;

      // Извлекаем имена файлов из разных возможных полей или из URL
      const transcriptionFilename = sanitizeFilename(
        payload.transcription_filename ||
          payload.filename_transcription ||
          payload.transcription_file ||
          payload.transcription_name ||
          (tBlock?.filename || "") ||
          (payload.transcription_download_url ? extractFilenameFromUrl(payload.transcription_download_url) : "") ||
          (payload.transcription_url ? extractFilenameFromUrl(payload.transcription_url) : "") ||
          (downloadUrls?.transcription ? extractFilenameFromUrl(downloadUrls.transcription) : "")
      );

      const analysisFilename = sanitizeFilename(
        payload.analysis_filename ||
          payload.analysis_file ||
          payload.analysis_name ||
          (aBlock?.filename || "") ||
          (payload.analysis_download_url ? extractFilenameFromUrl(payload.analysis_download_url) : "") ||
          (payload.analysis_url ? extractFilenameFromUrl(payload.analysis_url) : "") ||
          (downloadUrls?.analysis ? extractFilenameFromUrl(downloadUrls.analysis) : "")
      );

      // Формируем финальные URL: приоритет — по имени файла через наши хелперы, иначе используем то, что пришло
      let transcriptionUrl = transcriptionFilename
        ? downloadTranscription(transcriptionFilename)
        : (payload.transcription_download_url || payload.transcription_url || downloadUrls?.transcription || tBlock?.download_url || tBlock?.url || null);

      let analysisUrl = analysisFilename
        ? downloadAnalysis(analysisFilename)
        : (payload.analysis_download_url || payload.analysis_url || downloadUrls?.analysis || aBlock?.download_url || aBlock?.url || null);

      // Фолбэки: ищем строки внутри payload по шаблонам
      const flattenStrings = (obj) => {
        const out = [];
        const stack = [obj];
        while (stack.length) {
          const v = stack.pop();
          if (v == null) continue;
          if (typeof v === "string") out.push(v);
          else if (Array.isArray(v)) stack.push(...v);
          else if (typeof v === "object") stack.push(...Object.values(v));
        }
        return out;
      };

      const allStrings = flattenStrings(payload);
      if (!transcriptionUrl) {
        const tUrl = allStrings.find((s) => /\/transcriptions?\//i.test(s));
        if (tUrl) transcriptionUrl = tUrl;
      }
      if (!analysisUrl) {
        const aUrl = allStrings.find((s) => /\/analysis\//i.test(s));
        if (aUrl) analysisUrl = aUrl;
      }

      // Кандидаты: любые URL/пути, которые выглядят релевантно
      const asUrl = (s) => typeof s === "string" && (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/"));
      const urlStrings = allStrings.filter(asUrl);
      const candidateTranscription = transcriptionUrl || urlStrings.find((s) => /transcrib|transcript|transcription/i.test(s));
      const candidateAnalysis = analysisUrl || urlStrings.find((s) => /analys|analysis/i.test(s));

      // Если имени нет, но URL нашли — попробуем извлечь имя
      const tf = transcriptionFilename || (transcriptionUrl ? extractFilenameFromUrl(transcriptionUrl) : "");
      const af = analysisFilename || (analysisUrl ? extractFilenameFromUrl(analysisUrl) : "");

      // Если URL не определены, но есть имена — соберём ссылки через API-хелперы
      if (!transcriptionUrl && tf) transcriptionUrl = downloadTranscription(tf);
      if (!analysisUrl && af) analysisUrl = downloadAnalysis(af);
      // Нормализуем возможные относительные ссылки к API-origin
      if (transcriptionUrl) transcriptionUrl = toAbsoluteApiUrl(transcriptionUrl);
      if (analysisUrl) analysisUrl = toAbsoluteApiUrl(analysisUrl);

      setResult({
        transcriptionUrl,
        analysisUrl,
        transcriptionFilename: tf,
        analysisFilename: af,
        transcriptionPreview: payload.transcription_preview || payload.transcription_text || payload.preview || "",
        raw: payload,
        rawFull: data,
        candidates: {
          transcription: candidateTranscription || null,
          analysis: candidateAnalysis || null,
          others: urlStrings.slice(0, 5),
        },
        success: data.success !== false,
      });
    } catch (err) {
      setError(err.message || "Ошибка запроса");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-xl mx-auto bg-white p-8 rounded-lg shadow-lg mb-8 border border-gray-200">
        <h1 className="text-2xl font-semibold mb-6">Тестовая транскрибация и анализ</h1>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите API ключ"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MP3 файл
            </label>
            <input
              type="file"
              accept="audio/mpeg,.mp3"
              onChange={handleFileChange}
              className="w-full text-sm text-gray-700"
            />
          </div>

          {error && (
            <div className="bg-red-100 text-red-800 p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="gradient-button text-white font-semibold rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-300 w-full flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                <span>Загрузка и анализ...</span>
              </>
            ) : (
              "Загрузить и проанализировать"
            )}
          </button>
        </form>
      </div>

      {result && (
        <div className="max-w-xl mx-auto bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Результаты обработки</h3>
              <p className="text-sm text-gray-500">Файл: {file?.name}</p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-end space-x-2">
              {result.transcriptionUrl && (
                <button
                  onClick={() => handleDownload(result.transcriptionUrl, result.transcriptionFilename)}
                  title="Скачать транскрипцию"
                  className="p-2 rounded-lg bg-gradient-to-br from-pink-500 to-purple-600 text-white hover:opacity-90 transition-opacity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </button>
              )}

              {result.analysisUrl && (
                <button
                  onClick={() => handleDownload(result.analysisUrl, result.analysisFilename)}
                  title="Скачать анализ"
                  className="p-2 rounded-lg bg-blue-500 text-white hover:opacity-90 transition-opacity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                </button>
              )}

              {!result.transcriptionUrl && result.candidates?.transcription && (
                <button
                  onClick={() => handleDownload(toAbsoluteApiUrl(result.candidates.transcription), sanitizeFilename(result.candidates.transcription))}
                  title="Скачать транскрипцию (альт.)"
                  className="p-2 rounded-lg bg-gradient-to-br from-pink-400 to-purple-500 text-white hover:opacity-90 transition-opacity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </button>
              )}

              {!result.analysisUrl && result.candidates?.analysis && (
                <button
                  onClick={() => handleDownload(toAbsoluteApiUrl(result.candidates.analysis), sanitizeFilename(result.candidates.analysis))}
                  title="Скачать анализ (альт.)"
                  className="p-2 rounded-lg bg-blue-400 text-white hover:opacity-90 transition-opacity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                </button>
              )}
            </div>

            {!result.transcriptionUrl && !result.analysisUrl && (
              <div className="mt-3 text-xs text-gray-600">
                Ссылки на скачивание не найдены.
              </div>
            )}

            {result.transcriptionPreview && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-medium mb-1 text-gray-500">
                  Предпросмотр транскрипции:
                </p>
                <p className="text-sm text-gray-800 whitespace-pre-line">
                  {result.transcriptionPreview}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestTranscribePage;
